// Local identity store — persists multiple SpacetimeDB identities for one
// browser+host+db. Inspired by intrepid-tides' captain's log; lets a single
// browser switch between identities (very useful for testing as multiple
// players locally). The everyday end-user flow (one identity, auto-login)
// is unchanged.
//
// Stored under `${HOST}/${DB_NAME}/identities` as JSON.

export type LocalIdentity = {
  tokenHash: string;          // 16-hex-char digest of the token; the picker key
  token: string;              // the actual SpacetimeDB token
  localLabel: string;         // user-given label; defaults to "Identity-N"
  lastUsedAt: number;         // unix ms
  lastKnownUserName?: string; // last seen User.name from the server
  lastKnownIsAdmin?: boolean; // last seen User.isAdmin
};

export type LocalIdentityStore = {
  identities: LocalIdentity[];
  activeTokenHash: string | null;
};

const EMPTY: LocalIdentityStore = { identities: [], activeTokenHash: null };

function storeKey(host: string, dbName: string): string {
  return `${host}/${dbName}/identities`;
}

function legacyKey(host: string, dbName: string): string {
  // The previous shape: a single auth_token under this key.
  return `${host}/${dbName}/auth_token`;
}

export async function tokenHash(token: string): Promise<string> {
  // 16 hex chars (64 bits) of SHA-256. Plenty for collision-free local
  // bookkeeping and short enough to read.
  const bytes = new TextEncoder().encode(token);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const hex = Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  return hex.slice(0, 16);
}

function read(host: string, dbName: string): LocalIdentityStore {
  try {
    const raw = localStorage.getItem(storeKey(host, dbName));
    if (raw) return JSON.parse(raw) as LocalIdentityStore;
  } catch {
    /* fall through to migration / empty */
  }
  return { ...EMPTY };
}

function write(host: string, dbName: string, store: LocalIdentityStore): void {
  localStorage.setItem(storeKey(host, dbName), JSON.stringify(store));
}

// One-shot migration from the pre-multi-identity localStorage shape.
// Idempotent: removes the legacy key after migration, so subsequent calls
// are no-ops.
export async function migrateLegacyIfNeeded(
  host: string,
  dbName: string
): Promise<void> {
  const current = read(host, dbName);
  if (current.identities.length > 0) return;

  const legacy = localStorage.getItem(legacyKey(host, dbName));
  if (!legacy) return;

  const hash = await tokenHash(legacy);
  const migrated: LocalIdentityStore = {
    identities: [
      {
        tokenHash: hash,
        token: legacy,
        localLabel: 'Identity-1',
        lastUsedAt: Date.now(),
      },
    ],
    activeTokenHash: hash,
  };
  write(host, dbName, migrated);
  localStorage.removeItem(legacyKey(host, dbName));
}

export function loadStore(host: string, dbName: string): LocalIdentityStore {
  return read(host, dbName);
}

export function saveStore(
  host: string,
  dbName: string,
  store: LocalIdentityStore
): void {
  write(host, dbName, store);
}

export function findActive(store: LocalIdentityStore): LocalIdentity | null {
  if (!store.activeTokenHash) return null;
  return store.identities.find(i => i.tokenHash === store.activeTokenHash) ?? null;
}

export function findByHash(
  store: LocalIdentityStore,
  hash: string
): LocalIdentity | null {
  return store.identities.find(i => i.tokenHash === hash) ?? null;
}

export function setActive(
  host: string,
  dbName: string,
  hash: string | null
): LocalIdentityStore {
  const store = read(host, dbName);
  store.activeTokenHash = hash;
  write(host, dbName, store);
  return store;
}

export async function upsertIdentity(
  host: string,
  dbName: string,
  token: string,
  patch: Partial<Omit<LocalIdentity, 'tokenHash' | 'token'>> = {}
): Promise<LocalIdentity> {
  const hash = await tokenHash(token);
  const store = read(host, dbName);
  const existing = store.identities.find(i => i.tokenHash === hash);

  let row: LocalIdentity;
  if (existing) {
    row = { ...existing, ...patch, lastUsedAt: Date.now() };
    store.identities = store.identities.map(i =>
      i.tokenHash === hash ? row : i
    );
  } else {
    const n = store.identities.length + 1;
    row = {
      tokenHash: hash,
      token,
      localLabel: patch.localLabel ?? `Identity-${n}`,
      lastUsedAt: Date.now(),
      ...patch,
    };
    store.identities = [...store.identities, row];
  }

  // First identity becomes active automatically.
  if (!store.activeTokenHash) store.activeTokenHash = hash;

  write(host, dbName, store);
  return row;
}

export function renameIdentity(
  host: string,
  dbName: string,
  hash: string,
  label: string
): LocalIdentityStore {
  const store = read(host, dbName);
  store.identities = store.identities.map(i =>
    i.tokenHash === hash ? { ...i, localLabel: label } : i
  );
  write(host, dbName, store);
  return store;
}

export function forgetIdentity(
  host: string,
  dbName: string,
  hash: string
): LocalIdentityStore {
  const store = read(host, dbName);
  store.identities = store.identities.filter(i => i.tokenHash !== hash);
  if (store.activeTokenHash === hash) store.activeTokenHash = null;
  write(host, dbName, store);
  return store;
}

// ---------------------------------------------------------------------------
// Export / import — convenience for testing the same identity across local
// and maincloud. Not security: token files are sensitive.
// ---------------------------------------------------------------------------

export type IdentityExport = {
  type: 'heartwood-identity';
  version: 1;
  token: string;
  localLabel?: string;
  lastKnownUserName?: string;
  lastKnownIsAdmin?: boolean;
  exportedAt: number;
  checksum: string;
};

async function checksum(token: string): Promise<string> {
  // Use a fresh SHA-256 of the token + an export salt; checksum is just a
  // sanity check that the file hasn't been corrupted, not a security artifact.
  const bytes = new TextEncoder().encode(`heartwood-export-v1::${token}`);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 32);
}

export async function buildExport(id: LocalIdentity): Promise<IdentityExport> {
  return {
    type: 'heartwood-identity',
    version: 1,
    token: id.token,
    localLabel: id.localLabel,
    lastKnownUserName: id.lastKnownUserName,
    lastKnownIsAdmin: id.lastKnownIsAdmin,
    exportedAt: Date.now(),
    checksum: await checksum(id.token),
  };
}

export async function validateExport(
  candidate: unknown
): Promise<IdentityExport> {
  if (
    typeof candidate !== 'object' ||
    candidate === null ||
    (candidate as { type?: unknown }).type !== 'heartwood-identity'
  ) {
    throw new Error('not a Heartwood identity export');
  }
  const obj = candidate as IdentityExport;
  if (obj.version !== 1) throw new Error(`unsupported version: ${obj.version}`);
  if (typeof obj.token !== 'string' || !obj.token) {
    throw new Error('missing token');
  }
  if (typeof obj.checksum !== 'string') throw new Error('missing checksum');
  const expected = await checksum(obj.token);
  if (obj.checksum !== expected) throw new Error('checksum mismatch');
  return obj;
}
